import React from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { t } from '../config';

const MASK_SHAPES = [
  {
    name: 'Circle',
    clipFunc: (ctx, w, h) => {
      ctx.beginPath();
      ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.closePath();
    },
    preview: 'M50,5 A45,45 0 1,1 50,95 A45,45 0 1,1 50,5 Z',
    clipId: 'circle',
  },
  {
    name: 'Triangle',
    clipFunc: (ctx, w, h) => {
      ctx.beginPath();
      ctx.moveTo(w / 2, 0);
      ctx.lineTo(w, h);
      ctx.lineTo(0, h);
      ctx.closePath();
    },
    preview: 'M50,5 L95,95 L5,95 Z',
    clipId: 'triangle',
  },
  {
    name: 'Star',
    clipFunc: (ctx, w, h) => {
      const cx = w / 2, cy = h / 2;
      const outerR = Math.min(w, h) / 2;
      const innerR = outerR * 0.4;
      const points = 5;
      ctx.beginPath();
      for (let i = 0; i < points * 2; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const angle = (Math.PI * i) / points - Math.PI / 2;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
    },
    preview: 'M50,5 L61,38 L97,38 L68,60 L79,95 L50,73 L21,95 L32,60 L3,38 L39,38 Z',
    clipId: 'star',
  },
  {
    name: 'Heart',
    clipFunc: (ctx, w, h) => {
      ctx.beginPath();
      const topY = h * 0.3;
      ctx.moveTo(w / 2, h * 0.9);
      ctx.bezierCurveTo(w * 0.1, h * 0.55, -w * 0.1, topY * 0.5, w * 0.25, topY * 0.3);
      ctx.bezierCurveTo(w * 0.4, 0, w / 2, topY * 0.6, w / 2, topY);
      ctx.bezierCurveTo(w / 2, topY * 0.6, w * 0.6, 0, w * 0.75, topY * 0.3);
      ctx.bezierCurveTo(w * 1.1, topY * 0.5, w * 0.9, h * 0.55, w / 2, h * 0.9);
      ctx.closePath();
    },
    preview: 'M50,88 C15,55 -5,20 25,10 C40,3 50,15 50,25 C50,15 60,3 75,10 C105,20 85,55 50,88 Z',
    clipId: 'heart',
  },
  {
    name: 'Hexagon',
    clipFunc: (ctx, w, h) => {
      const cx = w / 2, cy = h / 2;
      const r = Math.min(w, h) / 2;
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const angle = (Math.PI * 2 * i) / 6 - Math.PI / 2;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
    },
    preview: 'M50,5 L93,27 L93,73 L50,95 L7,73 L7,27 Z',
    clipId: 'hexagon',
  },
  {
    name: 'Diamond',
    clipFunc: (ctx, w, h) => {
      ctx.beginPath();
      ctx.moveTo(w / 2, 0);
      ctx.lineTo(w, h / 2);
      ctx.lineTo(w / 2, h);
      ctx.lineTo(0, h / 2);
      ctx.closePath();
    },
    preview: 'M50,5 L95,50 L50,95 L5,50 Z',
    clipId: 'diamond',
  },
  {
    name: 'Pentagon',
    clipFunc: (ctx, w, h) => {
      const cx = w / 2, cy = h / 2;
      const r = Math.min(w, h) / 2;
      ctx.beginPath();
      for (let i = 0; i < 5; i++) {
        const angle = (Math.PI * 2 * i) / 5 - Math.PI / 2;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
    },
    preview: 'M50,5 L95,38 L77,95 L23,95 L5,38 Z',
    clipId: 'pentagon',
  },
  {
    name: 'Rounded Rect',
    clipFunc: (ctx, w, h) => {
      const r = Math.min(w, h) * 0.15;
      ctx.beginPath();
      ctx.moveTo(r, 0);
      ctx.lineTo(w - r, 0);
      ctx.quadraticCurveTo(w, 0, w, r);
      ctx.lineTo(w, h - r);
      ctx.quadraticCurveTo(w, h, w - r, h);
      ctx.lineTo(r, h);
      ctx.quadraticCurveTo(0, h, 0, h - r);
      ctx.lineTo(0, r);
      ctx.quadraticCurveTo(0, 0, r, 0);
      ctx.closePath();
    },
    preview: 'M15,5 L85,5 Q95,5 95,15 L95,85 Q95,95 85,95 L15,95 Q5,95 5,85 L5,15 Q5,5 15,5 Z',
    clipId: 'rounded-rect',
  },
  {
    name: 'Cross',
    clipFunc: (ctx, w, h) => {
      const t = 0.3;
      ctx.beginPath();
      ctx.moveTo(w * t, 0);
      ctx.lineTo(w * (1 - t), 0);
      ctx.lineTo(w * (1 - t), h * t);
      ctx.lineTo(w, h * t);
      ctx.lineTo(w, h * (1 - t));
      ctx.lineTo(w * (1 - t), h * (1 - t));
      ctx.lineTo(w * (1 - t), h);
      ctx.lineTo(w * t, h);
      ctx.lineTo(w * t, h * (1 - t));
      ctx.lineTo(0, h * (1 - t));
      ctx.lineTo(0, h * t);
      ctx.lineTo(w * t, h * t);
      ctx.closePath();
    },
    preview: 'M30,5 L70,5 L70,30 L95,30 L95,70 L70,70 L70,95 L30,95 L30,70 L5,70 L5,30 L30,30 Z',
    clipId: 'cross',
  },
];

// Export shapes so the canvas renderer can look them up
export { MASK_SHAPES };

const ImageMaskPanel = observer(({ store }) => {
  const panel = store._toolbarPanel;
  if (!panel || panel.type !== 'mask') return null;

  const element = store.getElementById(panel.elementId);
  if (!element) return null;

  const handleClose = () => store.closeToolbarPanel();

  const applyMask = (shape) => {
    element.set({ clipSrc: shape.clipId });
    store.history.addUndoState();
  };

  const removeMask = () => {
    element.set({ clipSrc: '' });
    store.history.addUndoState();
  };

  const hasMask = !!element.clipSrc;

  return (
    <Box
      sx={{
        width: 280,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: '#fff',
        borderRight: '1px solid #e0e0e0',
        flexShrink: 0,
        p: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('toolbar.clip', 'Apply Mask')}</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Image preview */}
      {element.src && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <img
            src={element.src}
            alt="Selected"
            style={{
              maxWidth: '100%',
              maxHeight: 120,
              objectFit: 'contain',
              borderRadius: 6,
              border: '1px solid #e0e0e0',
            }}
          />
        </Box>
      )}

      {hasMask && (
        <Box
          onClick={removeMask}
          sx={{
            mb: 2,
            py: 1,
            textAlign: 'center',
            cursor: 'pointer',
            borderRadius: '6px',
            border: '1px solid #d32f2f',
            color: '#d32f2f',
            fontWeight: 600,
            fontSize: 13,
            '&:hover': { backgroundColor: '#fbe9e7' },
          }}
        >
          {t('toolbar.removeMask', 'Remove mask')}
        </Box>
      )}

      {/* Shape grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
        {MASK_SHAPES.map((shape) => {
          const isActive = element.clipSrc === shape.clipId;
          return (
            <Box
              key={shape.clipId}
              onClick={() => applyMask(shape)}
              sx={{
                cursor: 'pointer',
                textAlign: 'center',
                borderRadius: '8px',
                border: isActive ? '2px solid #0d83cd' : '2px solid transparent',
                p: 0.5,
                '&:hover': { backgroundColor: '#f5f5f5' },
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg viewBox="0 0 100 100" width="100%" height="100%">
                  <path d={shape.preview} fill="#e0e0e0" stroke="#999" strokeWidth="1" />
                </svg>
              </Box>
              <Typography variant="caption" sx={{ fontSize: 10 }}>{shape.name}</Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
});

ImageMaskPanel.displayName = 'ImageMaskPanel';
export { ImageMaskPanel };
